import { useState, useRef } from "react";
import { Modal, Tag, Descriptions, Alert, Spin, Divider, Select, Tooltip, Button } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  DollarOutlined,
  WarningOutlined,
  TeamOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DebtModal from "../transfers/DebtModal";
import GroupDetailModal from "../calendar/GroupDetailModal";
import useGroups from "../../hooks/useGroups";

function BusPassengersModal({ open, onClose, data, loading }) {
  const [companyFilter, setCompanyFilter] = useState(null);
  const [hotelFilter, setHotelFilter] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [debtModal, setDebtModal] = useState({ open: false, userId: null, type: null, extraInfo: null, transferId: null });
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const contentRef = useRef(null);

  const { getGroup, group, setGroup, loading: groupLoading } = useGroups();

  const handleOpenGroupModal = async (groupId) => {
    if (!groupId) return;
    await getGroup(groupId);
    setGroupModalVisible(true);
  };

  const handleCloseGroupModal = () => {
    setGroupModalVisible(false);
    setGroup(null);
  };

  const generatePDF = async () => {
    if (!contentRef.current) return;

    setGeneratingPdf(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;

      // Calculate how many pages we need
      const scaledHeight = imgHeight * ratio;
      const pageHeight = pdfHeight - 10; // margin
      let position = 0;
      let page = 0;

      while (position < scaledHeight) {
        if (page > 0) {
          pdf.addPage();
        }
        pdf.addImage(imgData, 'PNG', imgX, -position + 5, imgWidth * ratio, imgHeight * ratio);
        position += pageHeight;
        page++;
      }

      const fileName = `pasajeros_${busInfo.excursionName || 'bus'}_${busInfo.date || 'fecha'}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (!data && !loading) return null;

  const busInfo = data?.busInfo || {};
  const passengers = data?.passengers || [];
  const totals = data?.totals || {};
  const expenses = data?.expenses || [];
  const totalExpenses = data?.totalExpenses || 0;
  const commissionTotals = data?.commissionTotals || {
    company: { paid: 0 },
    coordinator: { paid: 0 },
    seller: { paid: 0 }
  };

  // Get unique companies and hotels for filters
  const companies = [...new Map(
    passengers
      .filter(p => p.companyId && p.companyName)
      .map(p => [p.companyId, { value: p.companyId, label: p.companyName }])
  ).values()];

  const hotels = [...new Map(
    passengers
      .filter(p => p.hotelId && p.hotelName)
      .map(p => [p.hotelId, { value: p.hotelId, label: p.hotelName }])
  ).values()];

  // Filter passengers
  const filteredPassengers = passengers.filter(p => {
    if (companyFilter && p.companyId !== companyFilter) return false;
    if (hotelFilter && p.hotelId !== hotelFilter) return false;
    return true;
  });

  const getUserTypeLabel = (userType) => {
    const labels = {
      OFFICE_WORKER: "Oficina",
      SELLER: "Vendedor",
      GUIDE: "Guía",
      COORDINATOR: "Coordinador",
    };
    return labels[userType] || userType;
  };

  const getUserTypeColor = (userType) => {
    const colors = {
      OFFICE_WORKER: "blue",
      SELLER: "purple",
      GUIDE: "green",
      COORDINATOR: "orange",
    };
    return colors[userType] || "default";
  };

  const getExtraInfo = (userType) => {
    if (userType === "GUIDE") return busInfo?.id;
    if (userType === "SELLER") return busInfo?.date;
    return busInfo?.id;
  };

  const renderPayment = (payment, passenger) => {
    const paymentDate = payment.createdAt
      ? dayjs(payment.createdAt).format("DD/MM HH:mm")
      : "-";

    const isPendingSettlement = payment.isExternal && !payment.isSettled;
    const isSettledExternal = payment.isExternal && payment.isSettled && payment.transferId;

    const handleOpenDebt = () => {
      // For COORDINATOR, extraInfo should be the passenger's group_id
      // For SELLER, extraInfo should be the group check-in date
      // For GUIDE, extraInfo should be the bus id
      let extraInfo;
      if (payment.receiver?.userType === "SELLER") {
        extraInfo = passenger.groupCheckin;
      } else if (payment.receiver?.userType === "COORDINATOR") {
        extraInfo = passenger.groupId;
      } else {
        extraInfo = getExtraInfo(payment.receiver?.userType);
      }

      setDebtModal({
        open: true,
        userId: payment.receiver?.id,
        type: `USER_${payment.receiver?.userType}`,
        extraInfo,
        transferId: null
      });
    };

    const handleOpenTransfer = () => {
      setDebtModal({
        open: true,
        userId: null,
        type: null,
        extraInfo: null,
        transferId: payment.transferId
      });
    };

    const isClickable = isPendingSettlement || isSettledExternal;
    const handleClick = isPendingSettlement ? handleOpenDebt : (isSettledExternal ? handleOpenTransfer : undefined);

    return (
      <div
        key={payment.id}
        className={`ml-4 py-2 border-l-2 border-gray-200 pl-3 ${isPendingSettlement ? 'cursor-pointer hover:bg-red-50 rounded-r' : ''} ${isSettledExternal ? 'cursor-pointer hover:bg-green-50 rounded-r' : ''}`}
        onClick={handleClick}
      >
        {/* Línea principal: Monto - Cobró: Nombre (Tipo) - Fecha */}
        <div className="flex items-center gap-1 text-sm flex-wrap">
          <DollarOutlined className="text-green-600" />
          <span className="font-bold">${payment.amount?.toLocaleString()}</span>
          <span className="text-gray-400 mx-1">-</span>
          <span className="text-gray-600">Cobró:</span>
          <span className="font-medium">{payment.receiver?.name || "Sin usuario"}</span>
          <Tag color={getUserTypeColor(payment.receiver?.userType)} size="small">
            {getUserTypeLabel(payment.receiver?.userType)}
          </Tag>
          <span className="text-gray-400 mx-1">-</span>
          <span className="text-gray-500">{paymentDate}</span>
        </div>

        {/* Línea de estado */}
        <div className="ml-5 mt-1 text-xs">
          {payment.isExternal ? (
            // Pago externo (vendedor, guía, coordinador)
            payment.isSettled && payment.settlement ? (
              <span className="text-green-600">
                <CheckCircleOutlined className="mr-1" />
                Rendido {dayjs(payment.settlement.date).format("DD/MM HH:mm")}
                {payment.settlement.receivedBy && (
                  <span> → {payment.settlement.receivedBy}</span>
                )}
              </span>
            ) : (
              <span className="text-red-500">
                <WarningOutlined className="mr-1" />
                Sin rendir (ver deuda)
              </span>
            )
          ) : (
            // Pago en oficina
            <span className="text-blue-500">
              <CheckCircleOutlined className="mr-1" />
              En oficina
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderPassenger = (passenger) => {
    const commissions = passenger.commissions || {};

    return (
      <div key={passenger.id} className="mb-4">
        {/* Passenger header */}
        <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
          <div className="flex items-center gap-3">
            <span className="font-medium">{passenger.name}</span>
            <Tag>{passenger.quantity}</Tag>
            {passenger.companyName && (
              <span
                className={`text-xs flex items-center gap-1 ${passenger.groupId ? 'text-blue-500 hover:text-blue-700 cursor-pointer' : 'text-gray-500'}`}
                onClick={() => passenger.groupId && handleOpenGroupModal(passenger.groupId)}
              >
                <TeamOutlined />
                {passenger.companyName}
              </span>
            )}
            {passenger.hotelName && (
              <span className="text-xs text-gray-400">| {passenger.hotelName}</span>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-500">
              Total: <span className="font-medium">${passenger.totalAmount?.toLocaleString()}</span>
            </span>
            <span className="text-green-600">
              Pagado: ${passenger.paidAmount?.toLocaleString()}
            </span>
            <span className={passenger.debtAmount > 0 ? "text-orange-500" : "text-gray-400"}>
              Debe: ${passenger.debtAmount?.toLocaleString() || 0}
            </span>
          </div>
        </div>

        {/* Payments list */}
        <div className="mt-2">
          {passenger.payments && passenger.payments.length > 0 ? (
            passenger.payments.map((payment) => renderPayment(payment, passenger))
          ) : (
            <div className="ml-4 text-gray-400 text-sm italic">Sin pagos registrados</div>
          )}
        </div>

        {/* Commissions row */}
        <div className="flex items-center gap-4 mt-2 ml-2 text-xs border-t pt-2">
          <span className="text-gray-400">Comisiones:</span>
          {/* Company commission */}
          {commissions.company?.paid ? (
            <span className="text-green-600">
              <CheckCircleOutlined className="mr-1" />
              Emp ${commissions.company.amount?.toLocaleString()}
            </span>
          ) : commissions.company?.rate === 0 ? (
            <Tooltip title="Empresa sin comisión configurada (0%)">
              <span className="text-gray-300 cursor-help">Emp -</span>
            </Tooltip>
          ) : (
            <Tooltip title="Comisión empresa pendiente de pago">
              <span className="text-orange-500 cursor-help">
                <ExclamationCircleOutlined className="mr-1" />
                Emp ${(commissions.company?.estimated || 0).toLocaleString()}
              </span>
            </Tooltip>
          )}
          {/* Coordinator commission */}
          {commissions.coordinator?.paid ? (
            <span className="text-green-600">
              <CheckCircleOutlined className="mr-1" />
              Coord ${commissions.coordinator.amount?.toLocaleString()}
            </span>
          ) : commissions.coordinator?.rate === 0 ? (
            <Tooltip title="Coordinador sin comisión configurada (0%)">
              <span className="text-gray-300 cursor-help">Coord -</span>
            </Tooltip>
          ) : (
            <Tooltip title="Comisión coordinador pendiente de pago">
              <span className="text-orange-500 cursor-help">
                <ExclamationCircleOutlined className="mr-1" />
                Coord ${(commissions.coordinator?.estimated || 0).toLocaleString()}
              </span>
            </Tooltip>
          )}
          {/* Seller commission */}
          {commissions.seller?.paid ? (
            <span className="text-green-600">
              <CheckCircleOutlined className="mr-1" />
              Vend ${commissions.seller.amount?.toLocaleString()}
            </span>
          ) : !commissions.seller?.hasRate ? (
            <Tooltip title="Vendedor sin comisión configurada para esta excursión">
              <span className="text-gray-300 cursor-help">Vend -</span>
            </Tooltip>
          ) : (
            <Tooltip title="Comisión vendedor pendiente de pago">
              <span className="text-orange-500 cursor-help">
                <ExclamationCircleOutlined className="mr-1" />
                Vend ${(commissions.seller?.estimated || 0).toLocaleString()}
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <span>
          Pasajeros - {busInfo.excursionName || "Sin excursión"}
          {busInfo.date && (
            <span className="text-gray-500 font-normal ml-2">
              ({dayjs(busInfo.date).format("DD/MM/YYYY")})
            </span>
          )}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={
        <div className="flex justify-end">
          <Button
            type="primary"
            danger
            icon={<FilePdfOutlined />}
            onClick={generatePDF}
            loading={generatingPdf}
          >
            Descargar PDF
          </Button>
        </div>
      }
      width="90vw"
      styles={{ body: { maxHeight: "80vh", overflowY: "auto", overflowX: "hidden" } }}
      centered
    >
      <Spin spinning={loading}>
        <div ref={contentRef} className="bg-white">
        {/* Bus Info */}
        <Descriptions size="small" column={2} className="mb-4">
          <Descriptions.Item label="Bus">{busInfo.busName || "-"}</Descriptions.Item>
          <Descriptions.Item label="Guía">{busInfo.guideName || "-"}</Descriptions.Item>
        </Descriptions>

        {/* Account Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-8">
            {/* Left column - Ingresos */}
            <div>
              <div className="text-sm font-semibold text-gray-600 mb-2 border-b pb-1">INGRESOS</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total a cobrar ({totals.totalPassengers || 0} pasajeros)</span>
                  <span className="font-medium">${(totals.totalAmount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-600">Cobrado</span>
                  <span className="font-medium text-green-600">${(totals.totalPaid || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-orange-500">Por cobrar</span>
                  <span className="font-medium text-orange-500">${(totals.totalDebt || 0).toLocaleString()}</span>
                </div>
                {totals.totalPendingSettlement > 0 && (
                  <div className="flex justify-between">
                    <span className="text-yellow-600">Sin rendir</span>
                    <span className="font-medium text-yellow-600">${(totals.totalPendingSettlement || 0).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right column - Egresos */}
            <div>
              <div className="text-sm font-semibold text-gray-600 mb-2 border-b pb-1">EGRESOS</div>
              <div className="space-y-1 text-sm">
                {/* Gastos del viaje - desglosados */}
                {expenses.filter(e => e.isExpense).map((expense) => (
                  <div key={expense.id} className="flex justify-between">
                    <span className="text-red-500">{expense.description}</span>
                    <span className="font-medium text-red-500">-${expense.amount?.toLocaleString()}</span>
                  </div>
                ))}
                {expenses.filter(e => e.isExpense).length > 0 && (
                  <div className="flex justify-between border-t border-dashed pt-1">
                    <span className="text-red-400 text-xs">Subtotal gastos</span>
                    <span className="font-medium text-red-400 text-xs">-${totalExpenses.toLocaleString()}</span>
                  </div>
                )}

                {/* Comisiones header */}
                <div className="flex justify-between text-xs text-gray-400 mt-2 border-t pt-2">
                  <span>Comisiones</span>
                  <div className="flex gap-4">
                    <span className="w-20 text-right">Pagado</span>
                    <span className="w-20 text-right">Estimado</span>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    Com. Empresa
                    {commissionTotals.company?.paid > 0 && <CheckCircleOutlined className="ml-1 text-green-500" />}
                  </span>
                  <div className="flex gap-4">
                    <span className="w-20 text-right font-medium text-green-600">
                      -${(commissionTotals.company?.paid || 0).toLocaleString()}
                    </span>
                    <span className="w-20 text-right text-gray-400">
                      -${(commissionTotals.company?.estimated || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    Com. Coordinador
                    {commissionTotals.coordinator?.paid > 0 && <CheckCircleOutlined className="ml-1 text-green-500" />}
                  </span>
                  <div className="flex gap-4">
                    <span className="w-20 text-right font-medium text-green-600">
                      -${(commissionTotals.coordinator?.paid || 0).toLocaleString()}
                    </span>
                    <span className="w-20 text-right text-gray-400">
                      -${(commissionTotals.coordinator?.estimated || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    Com. Vendedor
                    {commissionTotals.seller?.paid > 0 && <CheckCircleOutlined className="ml-1 text-green-500" />}
                  </span>
                  <div className="flex gap-4">
                    <span className="w-20 text-right font-medium text-green-600">
                      -${(commissionTotals.seller?.paid || 0).toLocaleString()}
                    </span>
                    <span className="w-20 text-right text-gray-400">
                      -${(commissionTotals.seller?.estimated || 0).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between border-t pt-1 mt-2">
                  <span className="font-semibold text-gray-700">Total egresos</span>
                  <div className="flex gap-4">
                    <span className="w-20 text-right font-bold text-red-600">
                      -${(totalExpenses + (commissionTotals.company?.paid || 0) + (commissionTotals.coordinator?.paid || 0) + (commissionTotals.seller?.paid || 0)).toLocaleString()}
                    </span>
                    <span className="w-20 text-right font-medium text-gray-400">
                      -${(totalExpenses + (commissionTotals.company?.estimated || 0) + (commissionTotals.coordinator?.estimated || 0) + (commissionTotals.seller?.estimated || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Balance */}
          <div className="border-t-2 border-gray-300 mt-4 pt-3 space-y-2">
            <div className="flex justify-between text-base">
              <span className="font-bold text-gray-700">GANANCIA NETA (Cobrado - Egresos pagados)</span>
              <span className={`font-bold text-lg ${((totals.totalPaid || 0) - totalExpenses - (commissionTotals.company?.paid || 0) - (commissionTotals.coordinator?.paid || 0) - (commissionTotals.seller?.paid || 0)) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                ${((totals.totalPaid || 0) - totalExpenses - (commissionTotals.company?.paid || 0) - (commissionTotals.coordinator?.paid || 0) - (commissionTotals.seller?.paid || 0)).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">GANANCIA ESPERADA (Total a cobrar - Egresos estimados)</span>
              <span className={`font-medium ${((totals.totalAmount || 0) - totalExpenses - (commissionTotals.company?.estimated || 0) - (commissionTotals.coordinator?.estimated || 0) - (commissionTotals.seller?.estimated || 0)) >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                ${((totals.totalAmount || 0) - totalExpenses - (commissionTotals.company?.estimated || 0) - (commissionTotals.coordinator?.estimated || 0) - (commissionTotals.seller?.estimated || 0)).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Warning if pending settlement */}
        {totals.totalPendingSettlement > 0 && (
          <Alert
            message={`$${totals.totalPendingSettlement.toLocaleString()} pendiente de rendición`}
            type="warning"
            showIcon
            className="mb-4"
          />
        )}

        <Divider className="my-3" />

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Empresa:</span>
            <Select
              allowClear
              placeholder="Todas"
              style={{ width: 180 }}
              options={companies}
              value={companyFilter}
              onChange={setCompanyFilter}
              size="small"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Hotel:</span>
            <Select
              allowClear
              placeholder="Todos"
              style={{ width: 180 }}
              options={hotels}
              value={hotelFilter}
              onChange={setHotelFilter}
              size="small"
            />
          </div>
          {(companyFilter || hotelFilter) && (
            <span className="text-xs text-gray-400 self-center">
              Mostrando {filteredPassengers.length} de {passengers.length} pasajeros
            </span>
          )}
        </div>

        {/* Passengers List */}
        <div>
          {filteredPassengers.length > 0 ? (
            filteredPassengers.map(renderPassenger)
          ) : (
            <div className="text-center text-gray-400 py-8">
              {passengers.length > 0 ? "No hay pasajeros con los filtros seleccionados" : "No hay pasajeros en este bus"}
            </div>
          )}
        </div>
        </div>
      </Spin>

      {/* Debt Modal */}
      <DebtModal
        open={debtModal.open}
        onClose={() => setDebtModal({ open: false, userId: null, type: null, extraInfo: null, transferId: null })}
        userId={debtModal.userId}
        type={debtModal.type}
        extraInfo={debtModal.extraInfo}
        transferId={debtModal.transferId}
      />

      {/* Group Detail Modal */}
      <GroupDetailModal
        visible={groupModalVisible}
        group={group}
        onClose={handleCloseGroupModal}
      />
    </Modal>
  );
}

export default BusPassengersModal;
